<script setup>
import { useRoute, useRouter } from 'vue-router'
import StaffExposureService from '@/services/quality/staffExposure.service'
import ExposureHero from '@/views/quality/infection-control/exposure/ExposureHero.vue'
import PepFollowupCard from '@/views/quality/infection-control/exposure/PepFollowupCard.vue'
import ExposureDetailsCard from '@/views/quality/infection-control/exposure/ExposureDetailsCard.vue'
import RiskPepCard from '@/views/quality/infection-control/exposure/RiskPepCard.vue'
import ExposureRecordCard from '@/views/quality/infection-control/exposure/ExposureRecordCard.vue'
import ExposureDrawer from '@/views/quality/infection-control/exposure/ExposureDrawer.vue'
import PepFollowupCompleteDialog from '@/views/quality/infection-control/exposure/PepFollowupCompleteDialog.vue'
import { PepFollowupStatus } from '@/enums/quality/pep-followup-status'
import { isFollowupOverdue } from '@/utils/quality/pep-followup'

definePage({
  meta: {
    subject: 'quality.infection_control.menu',
    action: 'staff_exposure',
  },
})

const route = useRoute()
const router = useRouter()

const exposure = ref(null)

const loadExposure = async () => {
  await StaffExposureService.show(
    route.params.id,
    data => {
      exposure.value = data
    },
    () => {
      router.push({ name: 'invalid-url', params: { code: 404 } })
    },
  )
}

await loadExposure()

const followups = computed(() => exposure.value?.pepFollowups ?? [])

const sortedFollowups = computed(() =>
  [...followups.value].sort((a, b) => (a.scheduled_date ?? '').localeCompare(b.scheduled_date ?? '')))

const nextFollowup = computed(() => {
  const overdue = sortedFollowups.value.filter(isFollowupOverdue)
  if (overdue.length) return overdue[0]

  const pending = sortedFollowups.value.filter(f => f.status === PepFollowupStatus.PENDING && !isFollowupOverdue(f))

  return pending[0] ?? null
})

// Complete / edit dialog (owned here so the hero and timeline share it)
const isDialogOpen = ref(false)
const activeFollowup = ref(null)

const openComplete = followup => {
  activeFollowup.value = followup
  isDialogOpen.value = true
}

const openNextFollowup = () => {
  if (nextFollowup.value) openComplete(nextFollowup.value)
}

const onSaved = () => {
  loadExposure()
}

// Edit exposure in a drawer on this page (instead of redirecting to the listing)
const isEditDrawerOpen = ref(false)

const goToEdit = () => {
  isEditDrawerOpen.value = true
}

const goToIncident = () => {
  const incidentId = exposure.value?.incident_id ?? exposure.value?.incident?.id
  if (incidentId) router.push({ name: 'app-quality-entry', params: { id: incidentId } })
}
</script>

<template>
  <section v-if="exposure">
    <!-- Hero -->
    <ExposureHero
      :exposure="exposure"
      :next-followup="nextFollowup"
      class="mb-6"
      @record-result="openNextFollowup"
    />

    <VRow>
      <!-- Main column: PEP timeline -->
      <VCol
        cols="12"
        md="7"
      >
        <PepFollowupCard
          :followups="followups"
          @complete="openComplete"
          @followup-updated="loadExposure"
        />
      </VCol>

      <!-- Sidebar -->
      <VCol
        cols="12"
        md="5"
      >
        <div class="d-flex flex-column gap-6">
          <ExposureDetailsCard
            :exposure="exposure"
            @edit="goToEdit"
            @view-incident="goToIncident"
          />
          <RiskPepCard :exposure="exposure" />
          <ExposureRecordCard :exposure="exposure" />
        </div>
      </VCol>
    </VRow>

    <PepFollowupCompleteDialog
      v-model="isDialogOpen"
      :followup="activeFollowup ?? {}"
      @saved="onSaved"
    />

    <ExposureDrawer
      v-model:is-drawer-open="isEditDrawerOpen"
      :form-data="exposure"
      @refetch-listing="loadExposure"
    />
  </section>
</template>
